import * as THREE from "three";
import SticeArcWall from '../../../3ds/bin/stice/SticeArcWall';
import * as global from '../../../data/global';
import { CustomText3D } from "../../_shared/CustomText3D";
import CableNew from '../../../3ds/bin/cable/CableNew';
import FanBin3d from '../../../3ds/bin/fan/fan3d';
import { siteUsesEditorLayoutAxes } from '../../../components/layoutPreviewView';
import {
    flatCableRadiansToNorth,
    flatBinProductionRotationCorrectionRad,
    flatFootprintRotationBakedInGeometry,
    flatBinUsesCatalogCableScale,
    orientedLayoutFootprintFt,
    btsCableRadiansFromDeg,
    flatProductionGroupRotationY,
    flatProductionCableRotationRad,
    catalogNorthCompassBasePositions,
    flatCompassHalfExtentsFt,
} from '../../../components/siteLayoutStice.js';
import {
    buildBinLocalPolygonFromLayoutSides,
    layoutBinMatchesDropdownId,
    findLayoutBinForSiteBin,
} from '../../../components/layoutTableProxy';
import { useEffect } from "react";

const FLAT_COMPASS_Y = -2.25;
const FLAT_COMPASS_MARGIN_FT = 10;

const collectSticeBinMeshes = (binGroup) => {
    const meshes = [];
    if (!binGroup?.traverse) return meshes;
    binGroup.traverse((child) => {
        if (child.isMesh) meshes.push(child);
    });
    return meshes;
};

/** Legacy flat compass: catalog length on Z, width on X; spin labels with wall rotation. */
const catalogNorthCompassPositions = (layoutBin, site) => {
    const { hw, hl } = flatCompassHalfExtentsFt(layoutBin, site);
    const bin =
        layoutBin?.bin ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        );
    const deg = bin?.degreesToNorth ?? layoutBin?.rotation ?? 0;
    const m = FLAT_COMPASS_MARGIN_FT;
    const base = {
        north: [0, FLAT_COMPASS_Y, -hl - m],
        south: [0, FLAT_COMPASS_Y, hl + m],
        east: [hw + m, FLAT_COMPASS_Y, 0],
        west: [-hw - m, FLAT_COMPASS_Y, 0],
    };
    const rad =
        -(deg * Math.PI) / 180 +
        flatBinProductionRotationCorrectionRad(layoutBin, site);
    if (Math.abs(rad) < 0.001) {
        return base;
    }
    return {
        north: rotateFlatCompassPosition(base.north, rad),
        south: rotateFlatCompassPosition(base.south, rad),
        east: rotateFlatCompassPosition(base.east, rad),
        west: rotateFlatCompassPosition(base.west, rad),
    };
};

/** Place N/S/E/W just outside the flat footprint (polygon already carries orientation). */
const flatCompassPositions = (layoutBin, site, layoutSides) => {
    const skipCatalogNorthSpin =
        siteUsesEditorLayoutRotation(site) ||
        flatFootprintRotationBakedInGeometry(layoutBin, site) ||
        !!orientedLayoutFootprintFt(layoutBin, site) ||
        flatBinUsesCatalogCableScale(layoutBin, site);

    // Production flats: catalog degreesToNorth + wall spin — not raw polygon AABB.
    if (!skipCatalogNorthSpin) {
        return catalogNorthCompassPositions(layoutBin, site);
    }

    let poly = layoutBin?.previewPolygonFt;
    if (!(poly?.length >= 3) && layoutSides?.length >= 3) {
        poly = buildBinLocalPolygonFromLayoutSides(layoutBin, layoutSides);
    }
    if (poly?.length >= 3) {
        let minX = Infinity;
        let maxX = -Infinity;
        let minZ = Infinity;
        let maxZ = -Infinity;
        poly.forEach((p) => {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minZ = Math.min(minZ, p.z);
            maxZ = Math.max(maxZ, p.z);
        });
        const cx = (minX + maxX) / 2;
        const cz = (minZ + maxZ) / 2;
        const m = FLAT_COMPASS_MARGIN_FT;
        return {
            north: [cx, FLAT_COMPASS_Y, minZ - m],
            south: [cx, FLAT_COMPASS_Y, maxZ + m],
            east: [maxX + m, FLAT_COMPASS_Y, cz],
            west: [minX - m, FLAT_COMPASS_Y, cz],
        };
    }

    const cableRad = cableRadiansToNorth(site, layoutBin) ?? 0;
    const { hw, hl } = flatCompassHalfExtentsFt(layoutBin, site);
    const m = FLAT_COMPASS_MARGIN_FT;
    return {
        north: rotateFlatCompassPosition([0, FLAT_COMPASS_Y, -hl - m], cableRad),
        south: rotateFlatCompassPosition([0, FLAT_COMPASS_Y, hl + m], cableRad),
        east: rotateFlatCompassPosition([hw + m, FLAT_COMPASS_Y, 0], cableRad),
        west: rotateFlatCompassPosition([-hw - m, FLAT_COMPASS_Y, 0], cableRad),
    };
};

const siteUsesEditorLayoutRotation = (site) =>
    !!(
        site?.isLayoutPreview ||
        site?.isLayoutPreviewSite ||
        site?.isLayoutPreview3d ||
        siteUsesEditorLayoutAxes(site) ||
        site?.layoutSavedFromSiteLayoutEditor
    );

const layoutBinHasBakedFlatOutline = (layoutBin) =>
    !layoutBin?.isRound &&
    (layoutBin.previewPolygonFt?.length >= 3 || layoutBin.loopClosed);

/** Match Site 3D / layout preview cable orientation for flat bins. */
const cableRadiansToNorth = (site, layoutBin) => {
    if (layoutBin && !layoutBin.isRound) {
        if (orientedLayoutFootprintFt(layoutBin, site)) {
            return null;
        }
        if (flatBinUsesCatalogCableScale(layoutBin, site)) {
            return null;
        }
        if (siteUsesEditorLayoutRotation(site)) {
            return flatCableRadiansToNorth(layoutBin, site);
        }
        if (flatFootprintRotationBakedInGeometry(layoutBin, site)) {
            return flatCableRadiansToNorth(layoutBin, site);
        }
        const groupSpin = flatProductionCableRotationRad(layoutBin, site);
        if (groupSpin != null) return groupSpin;
    }
    if (layoutBin?.isRound && siteUsesEditorLayoutRotation(site)) {
        return null;
    }
    const catalogDeg =
        layoutBin?.bin?.degreesToNorth ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        )?.degreesToNorth;
    const base = btsCableRadiansFromDeg(catalogDeg);
    const correction = flatBinProductionRotationCorrectionRad(layoutBin, site);
    if (base == null) return correction || null;
    return base + correction;
};

/** Parent group already spins production flats — avoid double-rotating cable XZ. */
const cableRadiansInsideFlatProductionGroup = (site, layoutBin) => {
    if (orientedLayoutFootprintFt(layoutBin, site)) {
        return null;
    }
    if (flatBinUsesCatalogCableScale(layoutBin, site)) {
        return null;
    }
    if (siteUsesEditorLayoutRotation(site)) {
        return flatCableRadiansToNorth(layoutBin, site);
    }
    return null;
};

/** Rotate compass label offsets on Y — keeps CustomText3D camera billboarding intact. */
const rotateFlatCompassPosition = (position, radians) => {
    if (!position?.length || !radians) return position;
    const [x, y, z] = position;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    return [x * cos - z * sin, y, x * sin + z * cos];
};

/** Match layout/API bin ids whether state is number or string (e.g. MUI Select value). */
const sameBinDataId = (a, b) => {
    if (a === undefined || a === null || b === undefined || b === null) return false;
    const na = parseInt(String(a), 10);
    const nb = parseInt(String(b), 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
    return String(a) === String(b);
};

export const BinStice = (props) => {
    const rawSel = global.gw.selectedSite.selectedBinId;
    const isBinWindowView = !!global.gw.selectedSite?.isBinWindowView;
    const showAllBins = !isBinWindowView && (rawSel === 0 || rawSel === "0");
    const selectedSiteBin = (props.site.bins || []).find(
        (b) =>
            sameBinDataId(b.binDataId, rawSel) || sameBinDataId(b.binId, rawSel)
    );
    const selectedLayoutBin = showAllBins
        ? null
        : findLayoutBinForSiteBin(
              props.site,
              selectedSiteBin || { binDataId: rawSel, binId: rawSel }
          );

    useEffect(() => {
        if (siteUsesEditorLayoutRotation(props.site)) {
            return;
        }

        const sel = global.gw.selectedSite.selectedBinId;
        const bin = props.site.bins.find(y => sameBinDataId(y.binDataId, sel));
        const layoutBin = props.site.layoutBins?.find((lb) =>
            sameBinDataId(lb.binId, sel)
        );
        if (bin && 
            bin.type.toLowerCase().includes("flat") &&
            props.site.groupsRef.length > 0 && 
            props.site.groupsRef[0].children.length > 0 &&
            props.site.groupsRef[0].children[0].children.length > 1) {
            // Match flatProductionGroupRotationY — do not apply DegreesToNorth when the
            // layout footprint is long-on-X (cables stay in layout axes).
            let bin3dModel = props.site.groupsRef[0].children[0].children[1];
            if (bin3dModel) {
                const radiansToNorth = flatProductionGroupRotationY(
                    layoutBin,
                    props.site
                );
                bin3dModel.rotation.set(
                    bin3dModel.rotation.x,
                    radiansToNorth,
                    bin3dModel.rotation.z
                );
            }
        }
    });
    
    return (
        <group
            ref={el => {
                if (!el) return;
                props.site.siteRef = [];
                //props.site.groupsRef = [];
                //props.site.binsRef = [];
                //props.site.cablesRef = [];
                props.site.sensorsGroupsRef = [];
                // Don't clear sensorsRef - let it be populated by CableNew
                // props.site.sensorsRef = [];
                props.site.siteRef.push(el);
                props.site.offset = { x: el.position.x, y: el.position.y, z: el.position.z };
                if (!el.parent) return;
                props.site.sceneRef = el.parent;
            }}
        >
            {props.site.layoutGroups.map((layoutGroup) => (
                <group
                    key={`siteGroup_map_group_${layoutGroup.customerSiteLayoutGroupId}`}
                    //position={[layoutGroup.x, layoutGroup.y, -layoutGroup.z]}
                    ref={el => {
                        if (!el) return;
                        if (!props.site.groupsRef.includes(el)) {
                            el.name = layoutGroup.groupName;
                            el.groupId = layoutGroup.customerSiteLayoutGroupId;
                            el.isSelected = false;
                            el.width = layoutGroup.width;
                            el.length = layoutGroup.length;
                            el.height = layoutGroup.height;
                            props.site.groupsRef.push(el);
                        }
                        // Child bin refs are attached before this one, so the dropdown's new bin is
                        // already in binsRef - the group must not keep pointing at the old bin.
                        el.binsRef = props.site.binsRef.filter(x => x.groupId === layoutGroup.customerSiteLayoutGroupId);
                    }}
                >
                    {props.site.layoutBins.filter(x => x.customerSiteLayoutGroupId === layoutGroup.customerSiteLayoutGroupId).map((layoutBin) => {
                        const rawSelInner = global.gw.selectedSite.selectedBinId;
                        const layoutSidesForBin = props.site.layoutSides.filter(
                            (x) => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId
                        );
                        // Same rule as Site 3D / Stice.js. Neighbor-arc layoutSides on a round
                        // silo must not divert this to the stice polygon path (empty / axes walls).
                        const renderAsRound = !!layoutBin.isRound;
                        const isSelectedBin =
                            showAllBins ||
                            (selectedLayoutBin
                                ? sameBinDataId(
                                      selectedLayoutBin.customerSiteLayoutBinId,
                                      layoutBin.customerSiteLayoutBinId
                                  )
                                : layoutBinMatchesDropdownId(
                                      props.site,
                                      layoutBin,
                                      rawSelInner
                                  ));
                        return (isSelectedBin ?
                        renderAsRound ?
                            <group
                                key={`roundBin_map_group_${layoutBin.customerSiteLayoutBinId}`}
                                //position={[layoutBin.x, layoutBin.y, -layoutBin.z]}
                                layoutBin={layoutBin}
                                ref={el => {
                                    if (!el) return;
                                    if (!props.site.binsRef.includes(el)) {
                                        el.name = layoutBin.binName;
                                        el.binName = layoutBin.binName;
                                        el.layoutBin = layoutBin;
                                        el.groupId = layoutBin.customerSiteLayoutGroupId;
                                        el.binId = layoutBin.customerSiteLayoutBinId;
                                        el.binDataId = layoutBin.binId;
                                        el.isRound = !!layoutBin.isRound;
                                        el.width = layoutBin.width;
                                        el.length = layoutBin.length;
                                        el.height = layoutBin.height;
                                        el.isSelected = false;
                                        props.site.binsRef = props.site.binsRef
                                            .filter(b => b.binId !== el.binId)
                                            .concat(el);
                                    }
                                    el.meshes = collectSticeBinMeshes(el);
                                    if (el.isSelected) {
                                        global.applySelectedBinWallHighlight(el);
                                    }

                                    if (el.children && el.children[0] && el.children[0].children && el.children[0].children[0] && el.children[0].children[0].children && el.children[0].children[0].children[0] && el.children[0].children[0].children[0].children && el.children[0].children[0].children[0].children[0] && el.children[0].children[0].children[0].children[0].material) {
                                        let binTextMaterial = el.children[0].children[0].children[0].children[0].material;
                                        const binTextColor = global.gw.theme === 'light' ? "#878787" : "#898989";
                                        binTextMaterial.color.set(binTextColor);
                                        binTextMaterial.emissive.set(binTextColor);
                                        binTextMaterial.transparent = false;
                                        binTextMaterial.opacity = 1;
                                        binTextMaterial.needsUpdate = true;
                                    }
                                }}
                            >
                                <group position={[0, -2.25, 0]}>
                                    <CustomText3D
                                        scale={global.gw.selectedSite.binTextScale * 0.15}
                                        side={THREE.DoubleSide}
                                        position={[0, 0, 0]}
                                        meshProps={{
                                            material: new THREE.MeshStandardMaterial({
                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                side: THREE.FrontSide,
                                                toneMapped: false,
                                                envMapIntensity: 1,
                                                metalness: 0.0,
                                                transparent: true,
                                                opacity: 1,
                                            }),
                                        }}
                                    >
                                        {layoutBin.binName}
                                    </CustomText3D>
                                    {(() => {
                                        const binId = layoutBin.binId;
                                        const binRef = global.gw.selectedSite?.binsRef?.find(b => b.binDataId === binId);
                                        const lookupByDataId = binId != null ? (typeof binId === 'number' ? binId : parseInt(binId, 10)) : null;
                                        const lookupBySeqId = binRef?.binId != null ? (typeof binRef.binId === 'number' ? binRef.binId : parseInt(binRef.binId, 10)) : null;
                                        const binGradeData = (lookupByDataId != null && !isNaN(lookupByDataId) && global.gw.selectedSite?.binGrades?.has(lookupByDataId)) ? global.gw.selectedSite.binGrades.get(lookupByDataId)
                                            : (lookupBySeqId != null && !isNaN(lookupBySeqId) && global.gw.selectedSite?.binGrades?.has(lookupBySeqId)) ? global.gw.selectedSite.binGrades.get(lookupBySeqId) : null;
                                        let binDateRead = binGradeData?.dateRead ?? null;
                                        if (!binDateRead) {
                                            const binLayoutCables = props.site.layoutCables.filter(x => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId);
                                            if (binLayoutCables.length > 0 && global.gw.selectedSite?.cableGrades) {
                                                const dates = binLayoutCables.map(lc => {
                                                    const key = `${layoutBin.binId}_${lc.cableId}`;
                                                    const cg = global.gw.selectedSite.cableGrades.get(key);
                                                    return cg?.dateRead;
                                                }).filter(Boolean);
                                                binDateRead = dates.length > 0 ? dates.reduce((a, b) => new Date(a) > new Date(b) ? a : b) : null;
                                            }
                                        }
                                        const grade = binGradeData?.grade ?? global.gw.selectedSite?.siteGrade ?? null;
                                        const avgTime = binGradeData?.averageTimeToRead ?? null;
                                        const dateStr = binDateRead ? global.jsonDateToFormatDateOnly(binDateRead) : '';
                                        const showGradesOrDate = global.gw.selectedSite?.isGradesOn !== false;
                                        const showBinGrade = showGradesOrDate && !global.gw.selectedSite?.isCableFocused;
                                        const scale = global.gw.selectedSite.binTextScale * 0.15;
                                        return (
                                            <>
                                                {showBinGrade && dateStr ? (
                                                    <CustomText3D
                                                        scale={scale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {dateStr}
                                                    </CustomText3D>
                                                ) : null}
                                                {!global.gw.selectedSite?.isHms && grade != null && showBinGrade ? (
                                                    <CustomText3D
                                                        scale={scale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, (showBinGrade && dateStr) ? -8.0 : -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {`GQCS ${Math.round(grade)}%`}{avgTime != null && !isNaN(avgTime) ? ` ${avgTime}s` : ''}
                                                    </CustomText3D>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </group>
                                <group
                                    key={`roundWall_map_group_${layoutBin.customerSiteLayoutSideId}`}
                                    position={[0, 1.25 / 2.0 - 2, 0]}
                                >
                                    <SticeArcWall
                                        layoutBin={layoutBin}
                                        site={props.site}
                                        radiusTop={layoutBin.width / 2.0}
                                        radiusBottom={layoutBin.width / 2.0}
                                        height={1.25}
                                        radialSegments={60}
                                        heightSegments={20}
                                        openEnded={true}
                                        binId={layoutBin.customerSiteLayoutBinId}
                                        binDataId={layoutBin.binId}
                                        binName={layoutBin.binName}
                                        groupId={layoutBin.customerSiteLayoutGroupId}
                                    />
                                </group>
                                {props.site.layoutCables.filter(x => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId).map(layoutCable => (
                                    <CableNew
                                        key={`roundBin_cable_${layoutCable.customerSiteLayoutCableId}`}
                                        layoutCable={layoutCable}
                                        layoutSensors={props.site.layoutSensors.filter(x => x.customerSiteLayoutCableId === layoutCable.customerSiteLayoutCableId)}
                                        layoutBin={layoutBin}
                                        site={props.site}
                                        radiansToNorth={cableRadiansToNorth(props.site, layoutBin)}
                                    />
                                ))}
                                <FanBin3d
                                    layoutBin={layoutBin}
                                    site={props.site}
                                />
                                <CustomText3D
                                    scale={global.gw.selectedSite.binTextScale * 0.25}
                                    side={THREE.DoubleSide}
                                    position={[0, -2.25, -layoutBin.width / 2.0 - 3.0]}
                                    meshProps={{
                                        material: new THREE.MeshStandardMaterial({
                                            color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                            side: THREE.FrontSide,
                                            toneMapped: false,
                                            envMapIntensity: 1,
                                            metalness: 0.0,
                                            transparent: false,
                                            opacity: 1,
                                        }),
                                    }}
                                >
                                    N
                                </CustomText3D> 
                                <CustomText3D
                                    scale={global.gw.selectedSite.binTextScale * 0.25}
                                    side={THREE.DoubleSide}
                                    position={[0, -2.25, layoutBin.width / 2.0 + 3.0]}
                                    meshProps={{
                                        material: new THREE.MeshStandardMaterial({
                                            color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                            side: THREE.FrontSide,
                                            toneMapped: false,
                                            envMapIntensity: 1,
                                            metalness: 0.0,
                                            transparent: false,
                                            opacity: 1,
                                        }),
                                    }}
                                >
                                    S
                                </CustomText3D> 
                                <CustomText3D
                                    scale={global.gw.selectedSite.binTextScale * 0.25}
                                    side={THREE.DoubleSide}
                                    position={[layoutBin.width / 2.0 + 3.0, -2.25, 0]}
                                    meshProps={{
                                        material: new THREE.MeshStandardMaterial({
                                            color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                            side: THREE.FrontSide,
                                            toneMapped: false,
                                            envMapIntensity: 1,
                                            metalness: 0.0,
                                            transparent: false,
                                            opacity: 1,
                                        }),
                                    }}
                                >
                                    E
                                </CustomText3D> 
                                <CustomText3D
                                    scale={global.gw.selectedSite.binTextScale * 0.25}
                                    side={THREE.DoubleSide}
                                    position={[-layoutBin.width / 2.0 - 3.0, -2.25, 0]}
                                    meshProps={{
                                        material: new THREE.MeshStandardMaterial({
                                            color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                            side: THREE.FrontSide,
                                            toneMapped: false,
                                            envMapIntensity: 1,
                                            metalness: 0.0,
                                            transparent: false,
                                            opacity: 1,
                                        }),
                                    }}
                                >
                                    W
                                </CustomText3D>
                                <mesh
                                    visible={true}
                                    //position={[2, layoutBin.height / 2.0 - 6.0, 0]}
                                    position={[0, -2.25, -3.85]}
                                    rotation={[Math.PI / 2, 0, 0]}
                                >
                                    <ringGeometry args={[0.5, 0.6, 32]} />
                                    <meshBasicMaterial 
                                        attach="material" 
                                        color={global.gw.theme === 'light' ? 0x000000 : 0xFFFF00} 
                                        side={THREE.DoubleSide}
                                    />
                                </mesh>
                            </group>
                            :
                            <group
                                key={`sticeBin_map_group_${layoutBin.customerSiteLayoutBinId}`}
                                //position={[layoutBin.x, layoutBin.y, -layoutBin.z]}
                                layoutBin={layoutBin}
                                ref={el => {
                                    if (!el) return;
                                    if (!props.site.binsRef.includes(el)) {
                                        el.name = layoutBin.binName;
                                        el.binName = layoutBin.binName;
                                        el.layoutBin = layoutBin;
                                        el.groupId = layoutBin.customerSiteLayoutGroupId;
                                        el.binId = layoutBin.customerSiteLayoutBinId;
                                        el.binDataId = layoutBin.binId;
                                        el.isRound = !!layoutBin.isRound;
                                        el.width = layoutBin.width;
                                        el.length = layoutBin.length;
                                        el.height = layoutBin.height;
                                        el.isSelected = false;
                                        props.site.binsRef = props.site.binsRef
                                            .filter(b => b.binId !== el.binId)
                                            .concat(el);
                                    }
                                    el.meshes = collectSticeBinMeshes(el);
                                    if (el.isSelected) {
                                        global.applySelectedBinWallHighlight(el);
                                    }
                                    if (el.children && el.children[0] && el.children[0].children && el.children[0].children[0] && el.children[0].children[0].children && el.children[0].children[0].children[0] && el.children[0].children[0].children[0].children && el.children[0].children[0].children[0].children[0] && el.children[0].children[0].children[0].children[0].material) {
                                        let binTextMaterial = el.children[0].children[0].children[0].children[0].material;
                                        const binTextColor = global.gw.theme === 'light' ? "#878787" : "#898989";
                                        binTextMaterial.color.set(binTextColor);
                                        binTextMaterial.emissive.set(binTextColor);
                                        binTextMaterial.transparent = false;
                                        binTextMaterial.opacity = 1;
                                        binTextMaterial.needsUpdate = true;
                                    }
                                }}
                            >
                                <group position={[0, -2.25, 0]}>
                                    <CustomText3D
                                        scale={global.gw.selectedSite.binTextScale * ((layoutBin.bin?.type || "").toLowerCase().includes("flat") ? 0.18 : 0.15)}
                                        side={THREE.DoubleSide}
                                        position={[0, 0, 0]}
                                        meshProps={{
                                            material: new THREE.MeshStandardMaterial({
                                                color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                                side: THREE.FrontSide,
                                                toneMapped: false,
                                                envMapIntensity: 1,
                                                metalness: 0.0,
                                                transparent: false,
                                                opacity: 1,
                                            }),
                                        }}
                                    >
                                        {layoutBin.binName}
                                    </CustomText3D>
                                    {(() => {
                                        const binId = layoutBin.binId;
                                        const binRef = global.gw.selectedSite?.binsRef?.find(b => b.binDataId === binId);
                                        const lookupByDataId = binId != null ? (typeof binId === 'number' ? binId : parseInt(binId, 10)) : null;
                                        const lookupBySeqId = binRef?.binId != null ? (typeof binRef.binId === 'number' ? binRef.binId : parseInt(binRef.binId, 10)) : null;
                                        const binGradeData = (lookupByDataId != null && !isNaN(lookupByDataId) && global.gw.selectedSite?.binGrades?.has(lookupByDataId)) ? global.gw.selectedSite.binGrades.get(lookupByDataId)
                                            : (lookupBySeqId != null && !isNaN(lookupBySeqId) && global.gw.selectedSite?.binGrades?.has(lookupBySeqId)) ? global.gw.selectedSite.binGrades.get(lookupBySeqId) : null;
                                        let binDateRead = binGradeData?.dateRead ?? null;
                                        if (!binDateRead) {
                                            const binLayoutCables = props.site.layoutCables.filter(x => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId);
                                            if (binLayoutCables.length > 0 && global.gw.selectedSite?.cableGrades) {
                                                const dates = binLayoutCables.map(lc => {
                                                    const key = `${layoutBin.binId}_${lc.cableId}`;
                                                    const cg = global.gw.selectedSite.cableGrades.get(key);
                                                    return cg?.dateRead;
                                                }).filter(Boolean);
                                                binDateRead = dates.length > 0 ? dates.reduce((a, b) => new Date(a) > new Date(b) ? a : b) : null;
                                            }
                                        }
                                        const grade = binGradeData?.grade ?? global.gw.selectedSite?.siteGrade ?? null;
                                        const avgTime = binGradeData?.averageTimeToRead ?? null;
                                        const dateStr = binDateRead ? global.jsonDateToFormatDateOnly(binDateRead) : '';
                                        const showGradesOrDate = global.gw.selectedSite?.isGradesOn !== false;
                                        const showBinGrade = showGradesOrDate && !global.gw.selectedSite?.isCableFocused;
                                        const scale = global.gw.selectedSite.binTextScale * ((layoutBin.bin?.type || "").toLowerCase().includes("flat") ? 0.18 : 0.15);
                                        return (
                                            <>
                                                {showBinGrade && dateStr ? (
                                                    <CustomText3D
                                                        scale={scale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {dateStr}
                                                    </CustomText3D>
                                                ) : null}
                                                {!global.gw.selectedSite?.isHms && grade != null && showBinGrade ? (
                                                    <CustomText3D
                                                        scale={scale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, (showBinGrade && dateStr) ? -8.0 : -1.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {`GQCS ${Math.round(grade)}%`}{avgTime != null && !isNaN(avgTime) ? ` ${avgTime}s` : ''}
                                                    </CustomText3D>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </group>
                                <group
                                    rotation={[
                                        0,
                                        flatProductionGroupRotationY(
                                            layoutBin,
                                            props.site
                                        ),
                                        0,
                                    ]}
                                >
                                <group
                                    name={`sticeArcWall`}
                                    position={[0, -1.25 / 2 - 2, 0]}
                                >
                                    <SticeArcWall
                                        layoutBin={layoutBin}
                                        layoutSides={layoutSidesForBin}
                                        binId={layoutBin.customerSiteLayoutBinId}
                                        binDataId={layoutBin.binId}
                                        binName={layoutBin.binName}
                                        groupId={layoutBin.customerSiteLayoutGroupId}
                                        height={1.25}
                                        binWidth={layoutBin.width}
                                        binLength={layoutBin.length}
                                        site={props.site}
                                    />
                                </group>
                                {props.site.layoutCables.filter(x => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId).map(layoutCable => (
                                    <CableNew
                                        key={`sticeBin_cable_${layoutCable.customerSiteLayoutCableId}`}
                                        layoutCable={layoutCable}
                                        layoutSensors={props.site.layoutSensors.filter(x => x.customerSiteLayoutCableId === layoutCable.customerSiteLayoutCableId)}
                                        layoutBin={layoutBin}
                                        site={props.site}
                                        radiansToNorth={cableRadiansInsideFlatProductionGroup(props.site, layoutBin)}
                                    />
                                ))}
                                <FanBin3d
                                    layoutBin={layoutBin}
                                    site={props.site}
                                    insideRotatedGroup
                                />
                                {(() => {
                                    let northPosition = [];
                                    let southPosition = [];
                                    let eastPosition = [];
                                    let westPosition = [];
                                    let scale = global.gw.selectedSite.binTextScale * 0.25;
                                    const binType = (layoutBin.bin?.type || "").toLowerCase();
                                    const usesFlatCompass =
                                        binType.includes("flat") ||
                                        binType.includes("house") ||
                                        (!layoutBin.isRound &&
                                            (layoutBinHasBakedFlatOutline(layoutBin) ||
                                                siteUsesEditorLayoutRotation(props.site)));
                                    const groupRotatesFootprint =
                                        Math.abs(
                                            flatProductionGroupRotationY(
                                                layoutBin,
                                                props.site
                                            )
                                        ) > 0.001;
                                    if (usesFlatCompass) {
                                        const compass = groupRotatesFootprint
                                            ? catalogNorthCompassBasePositions(
                                                  layoutBin,
                                                  props.site
                                              )
                                            : flatCompassPositions(
                                                  layoutBin,
                                                  props.site,
                                                  layoutSidesForBin
                                              );
                                        northPosition = compass.north;
                                        southPosition = compass.south;
                                        eastPosition = compass.east;
                                        westPosition = compass.west;
                                    }
                                    else {
                                        const compass = catalogNorthCompassPositions(
                                            layoutBin,
                                            props.site
                                        );
                                        northPosition = compass.north;
                                        southPosition = compass.south;
                                        eastPosition = compass.east;
                                        westPosition = compass.west;
                                    }
                                    return (
                                        <>
                                            <CustomText3D
                                                scale={scale}
                                                side={THREE.DoubleSide}
                                                position={northPosition}
                                                meshProps={{
                                                    material: new THREE.MeshStandardMaterial({
                                                        color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                        side: THREE.FrontSide,
                                                        toneMapped: false,
                                                        envMapIntensity: 1,
                                                        metalness: 0.0,
                                                        transparent: true,
                                                        opacity: global.gw.theme === 'light' ? 0.2 : 0.045,
                                                    }),
                                                }}
                                            >
                                                N
                                            </CustomText3D> 
                                            <CustomText3D
                                                scale={scale}
                                                side={THREE.DoubleSide}
                                                position={southPosition}
                                                meshProps={{
                                                    material: new THREE.MeshStandardMaterial({
                                                        color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                        side: THREE.FrontSide,
                                                        toneMapped: false,
                                                        envMapIntensity: 1,
                                                        metalness: 0.0,
                                                        transparent: true,
                                                        opacity: global.gw.theme === 'light' ? 0.2 : 0.045,
                                                    }),
                                                }}
                                            >
                                                S
                                            </CustomText3D> 
                                            <CustomText3D
                                                scale={scale}
                                                side={THREE.DoubleSide}
                                                position={eastPosition}
                                                meshProps={{
                                                    material: new THREE.MeshStandardMaterial({
                                                        color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                        side: THREE.FrontSide,
                                                        toneMapped: false,
                                                        envMapIntensity: 1,
                                                        metalness: 0.0,
                                                        transparent: true,
                                                        opacity: global.gw.theme === 'light' ? 0.2 : 0.045,
                                                    }),
                                                }}
                                            >
                                                E
                                            </CustomText3D> 
                                            <CustomText3D
                                                scale={scale}
                                                side={THREE.DoubleSide}
                                                position={westPosition}
                                                meshProps={{
                                                    material: new THREE.MeshStandardMaterial({
                                                        color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                        side: THREE.FrontSide,
                                                        toneMapped: false,
                                                        envMapIntensity: 1,
                                                        metalness: 0.0,
                                                        transparent: true,
                                                        opacity: global.gw.theme === 'light' ? 0.2 : 0.045,
                                                    }),
                                                }}
                                            >
                                                W
                                            </CustomText3D>
                                        </>
                                    );
                                })()}
                                </group>
                            </group>
                        : null
                    );
                    })}
                </group>
            ))}
        </group>
    );
}

export default BinStice;